import React, { useCallback, useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import CircularProgress from '@mui/material/CircularProgress';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';

import { Assessment, AssessmentSurvey, AssessorAnswer } from '../../interfaces/assessment.interface';
import { Survey } from '../../interfaces/survey.interface';
import { fetchAssessment } from '../../services/assessments';
import { fetchSurvey } from '../../services/surveys';
import { buildFullQuestionnaireObject, fetchDetailedQuestionnaire } from '../../services/questionnaires';
import { initialBuildOutQuestionnaire } from '../../services/buildQuestionnaire';
import { Questionnaire, ReportVersion } from '../../interfaces/questionnaire.interface';
import { Question } from '../../interfaces/question.interface';
import { getCompany } from '../../services/company';
import User, { Badge, Company, CompanyApplication, Contact } from '../../interfaces/users.interface';
import { QuestionAnswer } from '../../interfaces/questionAnswer.interface';
import { appTheme } from '../Themes/light';
import { BestPractice } from '../../interfaces/bestPractice.interface';
import { useAppSelector } from '../../app/hooks';
import { WORKFLOW_MAP, fetchWorkflows } from '../../services/workflow';
import { NOT_IMPLEMENTED, PARTIALLY_IMPLEMENTED } from '../../services/surveyHelpers';
import  { getManyEvidence } from '../../services/questionAnswer';
import { getHighestRankAssessmentBadgeLabel, getLegendData, legacyOutputBPItems, outputBPItems } from '../../utils/reportOutput';
import { formatPdfData, usePdfReportQuestions } from '../../hooks/usePdfReportQuestions';

import { USER_TYPES } from '../../services/user';
import { ASSESSMENT_BADGE_TITLES, BLUE_BADGE_LABEL, BadgeImageData, GOLD_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL, SILVER_BADGE_LABEL } from '../../services/badge';
import { assessmentReportLegalLanguageSection, buildQuestionsFromDetailedQuestionnaire, generateConfidentialHeader, setAltBadgeImage } from '../../services/reports';
import { REPORT_TYPES } from '../../interfaces/reports.interface';
import { chooseFont } from '../../utils/charSetDetect';

export default function AssessmentReportMenuItemOption(props: { highestAssessmentBadge: BadgeImageData | null, assessmentId?: number | null, surveyId?: number }) {
  const {
    employeeCount,
    workFromHome,
    byod,
    thirdParty,
    contentTypes,
    physicalContent,
    timeframes,
    environments,
    dataCenter,
    replication,
    software,
    otherAssessments,
    zeroTrust,
    AIML,
    complianceTracking,
    KDM,
    pdfReportQuestionMap,
  } = usePdfReportQuestions();
  
  const { assessmentId, surveyId, highestAssessmentBadge } = props;
  const { user } = useAppSelector(state => state.user);
  const [isWorkflow53OrGreater, setIsWorkflow53OrGreater] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [assessment, setAssessment] = useState<Assessment | undefined>(undefined);
  const [survey, setSurvey] = useState<Survey | undefined>(undefined);
  const [baseline, setBaseline] = useState<Survey | undefined>(undefined);
  const [assessmentSurvey, setAssessmentSurvey] = useState<AssessmentSurvey | undefined>(undefined);
  const [company, setCompany] = useState<Company | undefined>(undefined);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | undefined>(undefined);
  const [questions, setQuestions] = useState<Question[] | undefined>(undefined);
  const [domainRollUps, setDomainRollUp] = useState<any>(undefined);
  const [assessmentBadge, setAssessmentBadge] = useState<string | null>(null);
  const [reportReady, setReportReady] = useState(false);
  const [site, setSite] = useState<Contact | undefined>(undefined);
  const [application, setApplication] = useState<CompanyApplication | undefined>(undefined);
  const [reportVersion, setReportVersion] = useState<ReportVersion>(ReportVersion.v5_2);
  const [bpDisplay, setBPDisplay] = useState<any>(undefined);
  const [countTotals, setCountTotals] = useState({
    bpCounts: {
      fi: 0,
      pi: 0,
      ni: 0,
      na: 0,
    },
    igCounts: {
      fi: 0,
      pi: 0,
      ni: 0,
      na: 0,
    },
  });
  const [PDFReport, setPDFReport] = useState<pdfMake.TCreatedPdf | undefined>(undefined);
  const [PDFReportTitle, setPDFReportTitle] = useState<string | undefined>(undefined);

  (pdfMake as any).vfs = (pdfFonts as any).vfs;

  const buildReport = async () => {
    if (!survey || !survey.questionnaire) {
      setReportLoading(false);
      return;
    }

    setReportLoading(true);
    const surQuestionnaire = survey.questionnaire as Questionnaire;

    getCompany(typeof survey.company === 'number' ? survey.company.toString() : survey.company.id!.toString()).then(comp => setCompany(comp));
    const questionnaireObjects = await buildFullQuestionnaireObject(surQuestionnaire.id);
    const builtQuestionnaire = initialBuildOutQuestionnaire({ ...questionnaireObjects, questionnaire: surQuestionnaire });
    setQuestionnaire(builtQuestionnaire.questionnaire);
    const detailedQuestionnaire = await fetchDetailedQuestionnaire(surQuestionnaire.id);
    setQuestions(
      buildQuestionsFromDetailedQuestionnaire(
        detailedQuestionnaire, surQuestionnaire,
      ),
    );
  };

  const bpHasPIandNI = (bp: BestPractice, assessorAnswers: AssessorAnswer[]) => {
    let found = false;

    (bp.questions as unknown[] as number[]).forEach(questionId => {
      const assessorAnswer = assessorAnswers.find(aa => aa.question === questionId);
      if (!assessorAnswer) return;
      if ([PARTIALLY_IMPLEMENTED, NOT_IMPLEMENTED].includes(assessorAnswer.answer as string)) {
        found = true;
      }
    });

    return found;
  };

  const loopQuestionnaire = useCallback(async () => {
    if (!survey || !assessment || !company || !questionnaire || !questions || questions.length === 0) {
      setReportLoading(false);
      return;
    }

    const questionAnswers = survey.questionAnswers;
    const curAssessment = (assessment.surveys as AssessmentSurvey[]).find(assSur => typeof assSur.survey === 'number' ? assSur.survey === survey.id : assSur.survey.id === survey.id);
    const assessorAnswers = curAssessment?.assessorAnswers as AssessorAnswer[];

    if (!questionAnswers || questionAnswers.length === 0 || !curAssessment || !assessorAnswers || assessorAnswers.length === 0) {
      setReportLoading(false);
      return;
    }
    setAssessmentSurvey(curAssessment);

    const rollups: any[] = [];
    const bps: any[] = [];
    const totalCounts = {
      bpCounts: {
        fi: 0,
        pi: 0,
        ni: 0,
        na: 0,
      },
      igCounts: {
        fi: 0,
        pi: 0,
        ni: 0,
        na: 0,
      },
    };

    for (const domain of questionnaire.domains) {
      const domainRollUp = {
        name: domain.title,
        bpCounts: { fi: 0, pi: 0, ni: 0, na: 0 },
        igCounts: { fi: 0, pi: 0, ni: 0, na: 0 },
      };

      for (const topic of domain.topics) {
        for (const bp of topic.bestPractices) {
          const curBP = { bp, domain, topic, questions: ([] as any[]) };

          // Loop through each question ID in bp.questions
          for (const questionId of bp.questions as unknown as number[]) {
            const question = questions.find(q => q.id === questionId);
            const answer = (survey.questionAnswers as QuestionAnswer[]).find(qa => qa.question === questionId);
            const assessorAnswer = assessorAnswers.find(aa => aa.question === questionId);
            if (!question || !assessorAnswer) continue;

            if (bpHasPIandNI(bp, assessorAnswers)) {
              // Fetch attachments if they exist
              if (assessorAnswer.attachments && assessorAnswer.attachments.length > 0) {
                try {
                  const attachments = await getManyEvidence(assessorAnswer.attachments);
                  assessorAnswer.evidenceAttachments = attachments;
                } catch (error) {
                  console.error(`Error fetching evidence for question ${questionId}:`, error);
                }
              }

              if (answer?.attachments && answer.attachments.length > 0) {
                try {
                  const attachments = await getManyEvidence(answer.attachments);
                  answer.evidenceAttachments = attachments;
                } catch (error) {
                  console.error(`Error fetching evidence for question ${questionId}:`, error);
                }
              }

              curBP.questions.push({ question, answer, assessorAnswer });
            }

            switch (assessorAnswer.answer) {
              case 'Fully Implemented':
                if (question.isBestPractice) {
                  domainRollUp.bpCounts.fi++;
                  totalCounts.bpCounts.fi++;
                }
                if (!question.isBestPractice) {
                  domainRollUp.igCounts.fi++;
                  totalCounts.igCounts.fi++;
                }
                break;
              case 'Partially Implemented':
                if (question.isBestPractice) {
                  domainRollUp.bpCounts.pi++;
                  totalCounts.bpCounts.pi++;
                }
                if (!question.isBestPractice) {
                  domainRollUp.igCounts.pi++;
                  totalCounts.igCounts.pi++;
                }
                break;
              case 'Not Implemented':
                if (question.isBestPractice) {
                  domainRollUp.bpCounts.ni++;
                  totalCounts.bpCounts.ni++;
                }
                if (!question.isBestPractice) {
                  domainRollUp.igCounts.ni++;
                  totalCounts.igCounts.ni++;
                }
                break;
              case 'Not Applicable':
                if (question.isBestPractice) {
                  domainRollUp.bpCounts.na++;
                  totalCounts.bpCounts.na++;
                }
                if (!question.isBestPractice) {
                  domainRollUp.igCounts.na++;
                  totalCounts.igCounts.na++;
                }
                break;
            }

          }
          if (curBP.questions.length > 0) bps.push(curBP);
        }
      }
      rollups.push(domainRollUp);
    }
    setDomainRollUp(rollups);
    setBPDisplay(bps);
    setCountTotals(totalCounts);
    if (employeeCount) {
      setReportLoading(false);
    }
  }, [survey, assessment, company, questionnaire, questions, employeeCount]);

  const setSiteOrApplication = () => {
    if (!company || !survey) return;

    const csite = survey.site ? company.locations.find(location => location.id === survey.site) : undefined;
    const capplication = survey.application ? company.companyApplications?.find(companyApplication => companyApplication.application.id === survey.application) : undefined;

    setSite(csite);
    setApplication(capplication);
  };

  useEffect(() => {
    if (!survey) return;
    buildReport();
  }, [survey]);

  useEffect(() => {
    if (!questions || !company || !questionnaire || !assessment) return;
    loopQuestionnaire();
    setSiteOrApplication();
  }, [questions, company, questionnaire, assessment]);

  // Last step to build report - Baseline summary items
  const fetchBaselineQuestions = async () => {
    if ((!site && !application) || !company || !baseline) return;

    const surQuestionnaire = baseline.questionnaire;
    const detailedQuestionnaire = await fetchDetailedQuestionnaire(typeof surQuestionnaire === 'number' ? surQuestionnaire : surQuestionnaire.id);
    if (typeof surQuestionnaire === 'number') {
      console.error('Survey questionnaire is a number, expected object');
      setReportLoading(false);
      return;
    }

    const baselineQuestions = buildQuestionsFromDetailedQuestionnaire(
      detailedQuestionnaire,
      surQuestionnaire,
    );
    if (!baselineQuestions.length || !baseline.questionAnswers?.length) return;

    // Loop once and set each question
    baselineQuestions.forEach(question => {
      const mapping = pdfReportQuestionMap.find(({ match }) => match(question.title));
      if (!mapping) return;

      const questionAnswer = (baseline.questionAnswers as QuestionAnswer[]).find(
        qa => (qa.question as number) === question.id,
      );
      if (!questionAnswer || !questionAnswer.answer?.length) return;

      if (mapping.multiSelect) {
        mapping.setter(
          question.answers.filter(answer => questionAnswer.answer!.includes(answer.id)),
        );
      } else {
        mapping.setter(
          question.answers.find(answer => answer.id === questionAnswer.answer![0]),
        );
      }
    });

    setReportLoading(false);
    setReportReady(true);
  };

  useEffect(() => {
    if ((!site && !application) || !company || !baseline || !bpDisplay) return;
    fetchBaselineQuestions();
  }, [baseline, site, application, company, bpDisplay]);

  const requestBuildReport = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (reportLoading) return;
    if (!assessmentId || !surveyId) {
      console.error('Assessment ID or Survey ID not provided');
      setReportLoading(false);
      return;
    }

    setReportLoading(true);
    fetchAssessment(assessmentId).then(ass => setAssessment(ass));
    fetchSurvey(surveyId).then(sur => setSurvey(sur));
    fetchWorkflows({ 'bp_survey': surveyId, assessment: assessmentId }).then(wf => {
      if (wf.length > 0) {
        const hasAssessmentBadge =
          (wf?.[0]?.badges as Badge[])?.some((b: Badge) => ASSESSMENT_BADGE_TITLES.has(b.title)) ?? false;
        const highestAssessmentBadgeLabel = getHighestRankAssessmentBadgeLabel(wf[0].badges as Badge[]);

        setAssessmentBadge(hasAssessmentBadge
          ? highestAssessmentBadgeLabel
          : setAltBadgeImage(wf[0]),
        );

        setIsWorkflow53OrGreater(!['v5.1', 'v5.2'].includes(WORKFLOW_MAP[wf[0].workflowType]?.version));
        fetchSurvey(wf[0].baseline!).then((bas) => {
          setBaseline(bas);
          const version = Object.values(ReportVersion).find(
            key => typeof bas.questionnaire !== 'number' && key === bas.questionnaire.version,
          );
          setReportVersion(version ? version : ReportVersion.v5_1);
        });
      }
    });
  };

  const outputOverviewBox = () => {
    if (!assessmentSurvey || !company) return;
    const output = {
      margin: [0, 0, 0, 30],
      table: {
        headerRows: 0,
        widths: ['*'],
        body: [
          [{
            border: [true, false, true, true],
            stack: [
              { text: [{ text: 'Assessment Scope:', bold: true }, ` ${assessmentSurvey.isOnPremise ? survey?.site ? 'Site' : 'On-Prem' : survey?.site ? 'Site' : 'Application'}${assessmentSurvey.isOnPremise && assessmentSurvey.isCloud ? ', ' : ''}${assessmentSurvey.isCloud ? 'Cloud' : ''}`] },
              { text: [{ text: 'Assessment Type:', bold: true }, ` ${assessmentSurvey.isInPerson ? 'On Site' : 'Remote'}`] },
            ],
          }],
        ],
      },
    };

    if (site) output.table.body[0][0].stack.push({ text: [{ text: 'Related Facility:', bold: true }, ` ${site.name}`] });
    if ((application && application.sites?.length)) output.table.body[0][0].stack.push({ text: [{ text: 'Related Facilities:', bold: true }, ` ${application.sites.map(csite => csite.name).join(', ')}`] });
    if ((site && site.services?.length)) output.table.body[0][0].stack.push({ text: [{ text: 'Services:', bold: true }, ` ${site.services.map(service => service.name).join(', ')}`] });
    if ((application && application.services?.length)) output.table.body[0][0].stack.push({ text: [{ text: 'Services:', bold: true }, ` ${application.services.map(service => service.name).join(', ')}`] });
    if (employeeCount?.text) output.table.body[0][0].stack.push({ text: [{ text: 'Number of Employees:', bold: true }, ` ${employeeCount?.text || ''}`] });
    const ownedApps = company?.companyApplications
      ?.filter(
        (ca) =>
          ca.application.company &&
            (typeof ca.application.company === 'number' && ca.application.company === company.id) ||
            (typeof ca.application.company !== 'number' && ca.application.company?.id === company.id),
      );
    if (site && ownedApps?.length) output.table.body[0][0].stack.push({ text: [{ text: 'Owned Applications:', bold: true }, ` ${ownedApps.map(app => app.application.name).join(', ')}`] });
    if (application) output.table.body[0][0].stack.push({ text: [{ text: 'Owned Application:', bold: true }, application.application.name ] });
    const thirdPartyApps = company?.companyApplications
      ?.filter(
        (ca) =>
          !ca.application.company ||
          (typeof ca.application.company === 'number' && ca.application.company !== company.id) ||
          (typeof ca.application.company !== 'number' && ca.application.company.id !== company.id),
      );
    if (site && thirdPartyApps?.length) output.table.body[0][0].stack.push({ text: [{ text: '3rd Party Licensed Applications:', bold: true }, ` ${thirdPartyApps.map(app => app.application.name).join(', ')}`] });
    return output;
  };

  const summaryOutputBox = () => {
    if (!assessmentSurvey || !company) return;
    let reportType = '';
    if (site) {
      reportType = 'site';
    } else if (application) {
      reportType = 'application';
    }
    if (!reportType) return;

    const output = {
      margin: [0, 0, 0, 30],
      table: {
        headerRows: 0,
        widths: ['*'],
        body: [
          [
            {
              border: [true, false, true, true],
              table: {
                headerRows: 0,
                widths: ['80%', '20%'],
                body: formatPdfData(reportVersion,
                  reportType,
                  {
                    employeeCount,
                    workFromHome,
                    byod,
                    thirdParty,
                    contentTypes,
                    physicalContent,
                    timeframes,
                    environments,
                    dataCenter,
                    replication,
                    software,
                    otherAssessments,
                    zeroTrust,
                    AIML,
                    complianceTracking,
                    KDM,
                  }),
              },
            },
          ],
        ],
      },
    };

    return output;
  };

  const assessorSummaryOutputBox = () => {
    if (!assessmentSurvey || !assessmentSurvey.assessorComment) return;
    const output = {
      margin: [0, 0, 0, 30],
      table: {
        headerRows: 0,
        widths: ['*'],
        body: [
          [{
            border: [true, false, true, true],
            stack: [
              { text: ' ' },
              { text: assessmentSurvey.assessorComment },
            ],
          }],
        ] },
    };

    return output;
  };

  const outputDomainRollUps = () => {
    const rollUpArray:any = [
      [ { text: '' },
        { text: 'Best Practice', colSpan: 4, alignment: 'center' },
        { text: '' },
        { text: '' },
        { text: '' },
        { text: reportVersion === ReportVersion.v5_1 ? 'Implementation Guidance' : 'Additional Recommendations', colSpan: 4, alignment: 'center' },
        { text: '' },
        { text: '' },
        { text: '' },
      ],
      [ { text: 'Security Domains', alignment: 'left' },
        { fillColor: appTheme.palette.success.main, text: 'F/I', color: 'white' },
        { fillColor: appTheme.palette.nonCompliant.main, text: 'P/I' },
        { fillColor: appTheme.palette.error.main, text: 'N/I', color: 'white' },
        { fillColor: '#ccc', text: 'N/A' },
        { fillColor: appTheme.palette.success.main, text: 'F/I', color: 'white' },
        { fillColor: appTheme.palette.nonCompliant.main, text: 'P/I' },
        { fillColor: appTheme.palette.error.main, text: 'N/I', color: 'white' },
        { fillColor: '#ccc', text: 'N/A' },
      ],
    ];
    domainRollUps.forEach((rollUp: any) => {
      const row = [
        { text: rollUp.name || '', alignment: 'left' },
        { text: rollUp.bpCounts.fi || 0 },
        { text: rollUp.bpCounts.pi || 0 },
        { text: rollUp.bpCounts.ni || 0 },
        { text: rollUp.bpCounts.na || 0 },
        { text: rollUp.igCounts.fi || 0 },
        { text: rollUp.igCounts.pi || 0 },
        { text: rollUp.igCounts.ni || 0 },
        { text: rollUp.igCounts.na || 0 },
      ];
      rollUpArray.push(row);
    });

    rollUpArray.push([
      { text: 'Totals', alignment: 'left' },
      countTotals.bpCounts.fi,
      countTotals.bpCounts.pi,
      countTotals.bpCounts.ni,
      countTotals.bpCounts.na,
      countTotals.igCounts.fi,
      countTotals.igCounts.pi,
      countTotals.igCounts.ni,
      countTotals.igCounts.na,
    ]);

    return rollUpArray;
  };

  const generatePDF = async () => {
    if (!(reportReady && assessmentSurvey && bpDisplay && domainRollUps && company && questionnaire && user)) return;
    const curDate = moment();

    pdfMake.fonts = {
      Roboto: {
        normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf',
      },
      NotoSansJP: {
        normal: 'https://cdn.jsdelivr.net/npm/typeface-notosans-jp@1.0.1/NotoSansJP-Regular.otf',
        bold: 'https://cdn.jsdelivr.net/npm/noto-sans-japanese@1.0.0/fonts/NotoSansJP-Bold.otf',
        italics: 'https://cdn.jsdelivr.net/npm/typeface-notosans-jp@1.0.1/NotoSansJP-Regular.otf',
        bolditalics: 'https://cdn.jsdelivr.net/npm/noto-sans-japanese@1.0.0/fonts/NotoSansJP-Bold.otf',
      },
      NotoSansKR: {
        normal: 'https://cdn.jsdelivr.net/npm/noto-sans-kr@0.1.1/fonts/NotoSans-Regular.woff2',
        bold: 'https://cdn.jsdelivr.net/npm/noto-sans-kr@0.1.1/fonts/NotoSans-Regular.woff2',
        italics: 'https://cdn.jsdelivr.net/npm/noto-sans-kr@0.1.1/fonts/NotoSans-Regular.woff2',
        bolditalics: 'https://cdn.jsdelivr.net/npm/noto-sans-kr@0.1.1/fonts/NotoSans-Regular.woff2',
      },
      NotoSansCH_Simplified: {
        normal: 'https://cdn.jsdelivr.net/npm/@openfonts/noto-sans-sc_chinese-simplified@1.44.9/files/noto-sans-sc-chinese-simplified-500.woff2',
        bold: 'https://cdn.jsdelivr.net/npm/@openfonts/noto-sans-sc_chinese-simplified@1.44.9/files/noto-sans-sc-chinese-simplified-500.woff2',
        italics: 'https://cdn.jsdelivr.net/npm/@openfonts/noto-sans-sc_chinese-simplified@1.44.9/files/noto-sans-sc-chinese-simplified-500.woff2',
        bolditalics: 'https://cdn.jsdelivr.net/npm/@openfonts/noto-sans-sc_chinese-simplified@1.44.9/files/noto-sans-sc-chinese-simplified-500.woff2',
      },
      MaterialSymbolsOutlined: {
        normal: 'https://fonts.gstatic.com/s/materialsymbolsoutlined/v75/kJF1BvYX7BgnkSrUwT8OhrdQw4oELdPIeeII9v6oDMzByHX9rA6RzaxHMPdY43zj-jCxv3fzvRNU22ZXGJpEpjC_1n-q_4MrImHCIJIZrDCvHOej.woff2',
      },
    };

    const docFont = chooseFont(company, site, application, questionnaire, assessmentSurvey);

    const { legendHeader, legendBody } = getLegendData();
    const imageMargin = [BLUE_BADGE_LABEL, 'nextgen_tpn_self_reported'].includes(assessmentBadge || '')
      ? [0, 0, 0, 10]
      : [0, -10, 0, 10];

    const docDefinition: any = {
      pageOrientation: 'landscape',
      defaultStyle: { font: docFont },
      content: [
        {
          stack: [
            { image: assessmentBadge, fit: [360, 360], margin: imageMargin },
            { text: 'CONTENT SECURITY ASSESSMENT', fontSize: 25 },
            ...(site ?
              [
                {
                  stack: [
                    company.name,
                    site.name,
                    site.address,
                    site.address2,
                    site.address3,
                    `${site.city}, ${site.state} ${site.zipcode}`,
                    site.country,
                  ], fontSize: 15, margin: [0, 20, 0, 20],
                },
                { text: 'Primary Contact Information', fontSize: 17 },
                `${site.primaryContact?.firstName} ${site.primaryContact?.lastName}`,
                site.primaryContact?.email,
                site.primaryContact?.contact?.phoneNumber,
              ]
              :
              [
                {
                  stack: [
                    company.name,
                    company.contact?.address,
                    company.contact?.address2,
                    company.contact?.address3,
                    `${company.contact?.city}, ${company.contact?.state} ${company.contact?.zipcode}`,
                    company.contact?.country,
                  ], fontSize: 15, margin: [0, 30, 0, 20],
                },
                { text: 'Primary Contact Information', fontSize: 17 },
                `${company.primaryContactUser?.firstName} ${company.primaryContactUser?.lastName}`,
                company.primaryContactUser?.email,
                company.primaryContactUser?.contact?.phoneNumber,
              ]
            ),
            {
              stack: [
                `Assessment Date: ${moment(assessmentSurvey.reportIssueDate).format('MMM DD, YYYY')}`,
                `Report Generated: ${moment().format('MMM DD, YYYY')}`,
                `Expiration Date: ${moment(assessmentSurvey.expirationDate).format('MMM DD, YYYY')}`,
                `TPN Assessor: ${(assessment?.assignedAssessor as User)?.firstName || ''} ${(assessment?.assignedAssessor as User)?.lastName || ''}`,
              ], fontSize: 12, margin: [0, 20, 0, 0],
            },
            { text: questionnaire.title, margin: [0, 10, 0, 0] },
          ], alignment: 'center', pageBreak: 'after',
        },
        assessmentReportLegalLanguageSection(assessmentSurvey, questionnaire, appTheme, (assessment?.assessor as Company)?.name || ''),
        {
          style: 'headerBG',
          table: {
            widths: '*',
            body: [
              [{
                border: [false, false, false, false],
                fillColor: appTheme.palette.primary.main,
                text: 'Overview',
              }],
            ],
          },
        },
        outputOverviewBox(),
        {
          style: 'headerBG',
          table: {
            widths: '*',
            body: [
              [{
                border: [false, false, false, false],
                fillColor: appTheme.palette.primary.main,
                text: 'Baseline Summary',
              }],
            ],
          },
        },
        summaryOutputBox(),
        assessmentSurvey.assessorComment && {
          style: 'headerBG',
          table: {
            widths: '*',
            body: [
              [{
                border: [false, false, false, false],
                fillColor: appTheme.palette.primary.main,
                text: 'Assessor Summary',
              }],
            ] },
        },
        assessmentSurvey.assessorComment && assessorSummaryOutputBox(),
        {
          style: 'headerBG',
          table: {
            widths: '*',
            body: [
              [{
                border: [false, false, false, false],
                fillColor: appTheme.palette.primary.main,
                text: 'Assessment Dashboard',
              }],
            ],
          },
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: outputDomainRollUps(),
          },
        },
        legendHeader,
        legendBody,
        {
          stack: isWorkflow53OrGreater ? outputBPItems(bpDisplay, user?.type || USER_TYPES.VENDOR_ADMIN) : legacyOutputBPItems(bpDisplay),
          margin: [0, 20, 0, 0],
        },
      ],
      images: {
        [GOLD_BADGE_LABEL]: `${window.location.origin}/assets/TPN_GoldLogoWhiteBkgrnd_1280wide.png`,
        [GOLD_STAR_BADGE_LABEL]: `${window.location.origin}/assets/TPN_GoldStarWhiteBkrnd_1024wide.png`,
        [GOLD_NEXT_GEN_BADGE_LABEL]: `${window.location.origin}/assets/TPN_GoldNextGenWhiteBkrnd_1024wide.png`,
        [SILVER_BADGE_LABEL]: `${window.location.origin}/assets/TPN_SilverWhiteBkrnd_1024wide.png`,
        [BLUE_BADGE_LABEL]: `${window.location.origin}/assets/TPN_BlueLogoWhiteBkgrnd_1280wide.png`,
        nextgen_tpn_self_reported: `${window.location.origin}/assets/TPN_NextGen_BlueLogoTransparent_1280wide.png`,
        WorkspacePremium: `${window.location.origin}/assets/Workspace-Premium.png`,
      },
      styles: {
        headerBG: {
          fontSize: 16,
          color: 'white',
        },
      },
      info: {
        title: `TPN-Assessment-${encodeURIComponent(company.name.replace(/\s+/g, '-'))}-${curDate.format('YYYY-MM-DD')}`,
        author: 'Trusted Partner Network',
      },
      watermark: { text: `TPN Assessment - ${user.email} - ${curDate.format('MMMM DD YYYY')}`, color: 'blue', opacity: 0.2, bold: true, italics: false },
      footer: function (currentPage: number, pageCount: number) { return { text: currentPage.toString() + ' of ' + pageCount, alignment: 'center', margin: [0, 10, 0, 0 ] }; },
      header: generateConfidentialHeader(
        company.name,
        curDate.format('MMM DD, YYYY'),
        REPORT_TYPES.ASSESSMENT,
        questionnaire.title,
        moment(assessmentSurvey.reportIssueDate).format('MMM DD, YYYY'),
      ),
    };

    // Set the PDFReport and PDFReportTitle for subsequent downloads.
    const pdfDoc: pdfMake.TCreatedPdf = pdfMake.createPdf(docDefinition);
    const pdfDocTitle = `TPN-Assessment-${encodeURIComponent(company.name.replace(/\s+/g, '-'))}-${curDate.format('YYYY-MM-DD')}.pdf`;
    setPDFReport(pdfDoc);
    setPDFReportTitle(pdfDocTitle);
    pdfMake.createPdf(docDefinition).download(pdfDocTitle);
  };

  useEffect(() => {
    if (reportReady) generatePDF().then(() => setReportReady(false));
  }, [reportReady]);

  return (
    <>
      {!surveyId ? (
        <MenuItem>
          <ListItemText primary="Could not find questionnaire" />
        </MenuItem>
      ) : (
        <MenuItem onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (PDFReport && PDFReportTitle) {
            PDFReport.download(PDFReportTitle);
          } else {
            requestBuildReport(e);
          }
        }}>
          <ListItemIcon>
            {reportLoading ? <CircularProgress size='20px' /> : 
              (<img
                src={highestAssessmentBadge?.src || '/assets/TPN_GoldLogoTransparent_1280.png'}
                alt={ highestAssessmentBadge?.alt || 'Gold Badge'}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  overflow: 'visible',
                  border: '1px solid #ddd',
                  backgroundColor: '#fff',
                }}
              />
              )}
          </ListItemIcon>
          <ListItemText primary='Assessment Report' />
        </MenuItem>
      )}
    </>
  );
}
